import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LinkButton } from "@/DesignSystem/Molecules/LinkButton";
import { MedicationHelper } from "@/Patient/Helpers/MedicationHelper";
import { MedicationCoverage } from "@/Patient/Models/Medication/Enums/Medication";
import { MedicationCoverages, MedicationResponse } from "@/Patient/Models/Medication/ResponseModels/MedicationResponse";

interface IPatientMedicationSearchDetails {
    referenceId: string;
    model: MedicationResponse;
    encounterSiteId: string;
    onExceptionClick?: (exceptionKey: string) => void;
    onSearchByActiveIngredientClick: (drugIdentificationNumber: string) => void;
    onLimitedUseCodeClick?: (code: string) => void;
    limitedUseCodes?: string[];
}

class PatientMedicationSearchDetails extends BaseReactComponent<IPatientMedicationSearchDetails> {
    public render(): JSX.Element {
        const provincesLookupView = this.getProvincesCoverage();
        const shouldDisplayRamq = this.shouldDisplayRamq(provincesLookupView);
        const shouldDisplayLimitedUseCodes = this.shouldDisplayLimitedUseCodes(provincesLookupView);

        return (
            <div className="p-body">
                {this.props.model.HasMonograph &&
                    this.renderRow(
                        "PharmacologicalClassLabel",
                        <div className="flex-container space-between">
                            <HyperLinkControl
                                text={this.props.model.CodeGenValue}
                                id={this.props.model.Id}
                                item={this.props.model.CodeGen}
                                operation={LocalPatientOperations.PrescriptionTab.MedicationDetails_GetVigilanceMonographie}
                                className="bold"
                            />
                            {this.renderSearchByActiveIngredient()}
                        </div>
                    )
                }
                {this.renderRow(
                    "CoverageLabel",
                    [this.renderProvinces(provincesLookupView)]
                )}
                {this.props.limitedUseCodes?.length > 0 && shouldDisplayLimitedUseCodes &&
                    this.renderRow(
                        "LimitedUseCodesLabel",
                        [this.renderLimitedUseCodes()]
                    )
                }
                {this.shouldDisplayNewBrunswickInsurancePlan(provincesLookupView) &&
                    this.renderRow(
                        "NewBrunswickDrugPlanLabel",
                        this.props.model.NewBrunswickInsurancePlan
                    )
                }
                {this.shouldDisplayQuebecClaimDIN(provincesLookupView) &&
                    this.renderRow(
                        "RAMQClaimDINLabel",
                        this.props.model.QuebecCoverageClaimDIN
                    )
                }
                {(this.props.model.FormException?.any() && shouldDisplayRamq) &&
                    this.renderRow(
                        "RAMQFormsLabel",
                        this.renderFormExceptionCodes()
                    )
                }
                {(this.props.model.IsException && this.props.model.CodeException?.any() && shouldDisplayRamq) &&
                    this.renderRow(
                        "RAMQExceptionCodesLabel",
                        this.renderExceptionCodes()
                    )
                }
            </div>
        );
    }

    public renderFormExceptionCodes(): JSX.Element {
        return (
            <div>
                {this.props.model.FormException.map((item, index) => (
                    <div key={item.Key + index} style={{ "fontWeight": "normal" }}>
                        {item.Key}
                    </div>
                ))}
            </div>
        );
    }

    public renderExceptionCodes(): JSX.Element {
        return (
            <table>
                <tbody>
                    {this.props.model.CodeException.map((item, index) => (
                        <tr key={item.Key + index} style={{ "fontWeight": "normal", borderBottom: "1px solid #ddd" }}>
                            <td style={{ "verticalAlign": "top", "width": "65px", paddingTop: "5px", paddingBottom: "10px" }}>
                                <a className="bold" onClick={() => this.props.onExceptionClick?.(item.Key)}>
                                    {item.Key}
                                </a>
                            </td>
                            <td style={{ "fontSize": "13px", paddingTop: "5px", paddingBottom: "10px" }}>
                                <div className="medication-details">
                                    <div className="content" dangerouslySetInnerHTML={{ __html: item.Value }} />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    public renderRow(label: string, element: React.ReactNode): JSX.Element {
        return (
            <div className="col-md-12 margin-md-top row">
                <span>{cacheStore.GetString(label)}</span>
                <div className="margin-md-left normal-text">
                    {element}
                </div>
            </div>
        );
    }

    public shouldComponentUpdate(nextProp: IPatientMedicationSearchDetails): boolean {
        const idChanged = this.props.referenceId !== nextProp.referenceId;
        const modelChanged = this.props.model !== nextProp.model;
        const limitedCodesChanged = this.props.limitedUseCodes !== nextProp.limitedUseCodes;
        return idChanged || modelChanged || limitedCodesChanged;
    }

    public getProvincesCoverage(): FlatLookupView[] {
        let cardsAvailableRegionLookups = (patientStore.getActivePatientHealthCards() || [])
            .map(x => cacheStore.GetLookupById(x.ProvinceLookupId));

        const currentSiteLookupId = cacheStore.GetSiteById(this.props.encounterSiteId)
            ?.Model
            .Adresses
            .firstOrDefault()
            ?.StateLookupId;

        const currentSiteLookup =
            cacheStore.GetLookupById(currentSiteLookupId)
            ?? cacheStore.GetDefaultRegionForActiveSite();

        if (!cardsAvailableRegionLookups.contains(currentSiteLookup))
            cardsAvailableRegionLookups.push(currentSiteLookup);

        const medicationCoverageLookupCodes = this.convertMedicationCoveragedToLookupCode(this.props.model.MedicationCoverages);

        return cardsAvailableRegionLookups
            .filter(item => medicationCoverageLookupCodes.contains(item.LookupCode));
    }

    public getLimitedUseCodeDescriptionUrl(code: string): string {
        return apiSettings.buildAppUrl(
            "api/vigilance/limitedUseCodeDescription",
            {
                auth: true,
                params: {
                    LimitedCode: code
                }
            }
        );
    }

    public renderLimitedUseCodes(): JSX.Element[] {
        const { limitedUseCodes, onLimitedUseCodeClick } = this.props;

        return limitedUseCodes?.map((code, idx) => {
            const isLastItem = idx === limitedUseCodes.length - 1;

            return (
                <div
                    key={code}
                    className="flex-row pad-top-10 flex-gap-xl"
                    style={{ paddingBottom: 5, borderBottom: isLastItem ? undefined : "1px solid #ddd", alignItems: "self-start" }}
                >
                    <LinkButton
                        label={code}
                        underline={false}
                        onClick={() => onLimitedUseCodeClick(code)}
                    />
                    <iframe
                        src={this.getLimitedUseCodeDescriptionUrl(code)}
                        height="100%"
                        width="100%"
                    />
                </div>
            );
        }) || [];
    }

    public renderProvinces(provinces: FlatLookupView[]): JSX.Element {
        let element = <div> -- </div>;
        const provinceIds = provinces?.map(lookup => lookup.LookupID);

        if (provinces?.any()) {
            element = (
                <div>
                    {provinceIds
                        .sort((a, b) => a.localeCompare(b))
                        .map((item, i) => (
                            <div key={item + i}>
                                {cacheStore.GetLocalizedValueByLookupId(item)}
                            </div>
                        ))}
                </div>
            );
        }
        return element;
    }

    public convertMedicationCoveragedToLookupCode(medicationCoverage: MedicationCoverages): string[] {
        if (!medicationCoverage) {
            return [];
        }

        let lookupCodes: string[] = [];

        if (medicationCoverage.AlbertaCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.push("CanadaState_AB");
        if (medicationCoverage.BritishColumbiaCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.push("CanadaState_BC");
        if (medicationCoverage.FirstPeoplesCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.concat("CanadaState_NT", "CanadaState_NU", "CanadaState_YT");
        if (medicationCoverage.ManitobaCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.push("CanadaState_MB");
        if (medicationCoverage.NewBrunswickCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.push("CanadaState_NB");
        if (medicationCoverage.NewfoundlandCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.push("CanadaState_NL");
        if (medicationCoverage.NovaScotiaCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.push("CanadaState_NS");
        if (medicationCoverage.OntarioCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.push("CanadaState_ON");
        if (medicationCoverage.PrinceEdwardIslandCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.push("CanadaState_PE");
        if (medicationCoverage.QuebecCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.push("CanadaState_QC");
        if (medicationCoverage.SaskatchewanCoverage !== MedicationCoverage.NotCovered)
            lookupCodes.push("CanadaState_SK");

        return lookupCodes;
    }

    public shouldDisplayNewBrunswickInsurancePlan(provinces: FlatLookupView[]): boolean {
        return !!this.props.model.NewBrunswickInsurancePlan
            && provinces
                ?.map(x => x.LookupCode)
                .contains("CanadaState_NB");
    }

    public shouldDisplayQuebecClaimDIN(provinces: FlatLookupView[]): boolean {
        return !!this.props.model.QuebecCoverageClaimDIN
            && this.props.model.QuebecCoverageClaimDIN !== "0"
            && this.shouldDisplayRamq(provinces);
    }

    public shouldDisplayRamq(provinces: FlatLookupView[]): boolean {
        return provinces
            ?.map(x => x.LookupCode)
            .contains("CanadaState_QC");
    }

    public shouldDisplayLimitedUseCodes(provinces: FlatLookupView[]): boolean {
        return provinces
            ?.map(x => x.LookupCode)
            .contains("CanadaState_ON");
    }

    private renderSearchByActiveIngredient(): JSX.Element {
        if (IsNullOrUndefined(this.props.model.Din)) {
            return null;
        }

        const drugIdentificationNumber: string = MedicationHelper.getMedicationDinOrName("", this.props.model?.Din);
        return (
            <a
                className="normal-text"
                onClick={() => this.props.onSearchByActiveIngredientClick(drugIdentificationNumber)}
            >
                {cacheStore.GetString("SearchByActiveIngredientLabel")}
            </a>
        );
    }
}

export { PatientMedicationSearchDetails };
